import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Modal, Form, InputNumber, Select, Input, Timeline, Tag, Space, message, Descriptions, Row, Col, Progress, Statistic, Typography } from 'antd';
import { TruckOutlined, CheckCircleOutlined, ClockCircleOutlined, RollbackOutlined, CloseCircleOutlined, ShopOutlined } from '@ant-design/icons';
import axiosInstance from '../../../axiosInstance';
import { useParams } from 'react-router-dom';

const { Option } = Select;
const { TextArea } = Input;
const { Paragraph } = Typography;

const OrderTracking = ({ saleId }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [trackingData, setTrackingData] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [historyVisible, setHistoryVisible] = useState(false);
  const [retailers, setRetailers] = useState([]);
  const { id } = useParams();

  useEffect(() => {
    if (id) {
      fetchTrackingData();
      fetchRetailers();
    }
    console.log(id);
  }, [id]);

  const fetchTrackingData = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(`/order-tracking/${id}`);
      setTrackingData(response.data);

      // Initialize selected items
      const items = response.data.tracking_summary.map(item => ({
        sale_item_id: item.item_id,
        quantity: 0,
        max_quantity: item.remaining_quantity
      }));
      setSelectedItems(items);
    } catch (error) {
      message.error('Failed to fetch tracking data');
    } finally {
      setLoading(false);
    }
  };

  const fetchRetailers = async () => {
    try {
      const response = await axiosInstance.get('/shops');
      setRetailers(response.data.shops || []);
    } catch (error) {
      console.error('Failed to fetch retailers');
    }
  };

  const handleStatusUpdate = async (values) => {
    setLoading(true);
    try {
      const payload = {
        status: values.status,
        items: selectedItems.filter(item => item.quantity > 0).map(item => ({
          sale_item_id: item.sale_item_id,
          quantity: item.quantity
        })),
        notes: values.notes,
      };

      if (payload.items.length === 0 && values.status !== 'closed') {
        message.error('Please enter quantities for at least one item');
        setLoading(false);
        return;
      }

      await axiosInstance.post(`/order-tracking/${id}/update-status`, payload);
      message.success('Order status updated successfully');
      setModalVisible(false);
      form.resetFields();
      fetchTrackingData();
    } catch (error) {
      message.error(error.response?.data?.error || 'Failed to update status');
    } finally {
      setLoading(false);
    }
  };

  const handleQuantityChange = (index, value) => {
    const newItems = [...selectedItems];
    newItems[index].quantity = value;
    setSelectedItems(newItems);
  };

  const getStatusColor = (status) => {
    const colors = {
      released: 'blue',
      picked: 'cyan',
      shipped: 'green',
      in_transit: 'orange',
      returned: 'red',
      closed: 'gray'
    };
    return colors[status] || 'default';
  };

  const getStatusIcon = (status) => {
    const icons = {
      released: <ClockCircleOutlined />,
      picked: <CheckCircleOutlined />,
      shipped: <TruckOutlined />,
      in_transit: <TruckOutlined />,
      returned: <RollbackOutlined />,
      closed: <CloseCircleOutlined />
    };
    return icons[status] || <ClockCircleOutlined />;
  };

  const getProgressPercent = (item) => {
    return ((item.shipped_quantity / item.total_quantity) * 100).toFixed(1);
  };

  const columns = [
    {
      title: 'Product',
      dataIndex: 'product_name',
      key: 'product_name',
      width: 200,
    },
    {
      title: 'Total Qty',
      dataIndex: 'total_quantity',
      key: 'total_quantity',
      width: 100,
      render: (qty) => <Tag color="blue">{qty}</Tag>
    },
    {
      title: 'Picked',
      dataIndex: 'picked_quantity',
      key: 'picked_quantity',
      width: 100,
      render: (qty) => <Tag color="cyan">{qty}</Tag>
    },
    {
      title: 'Shipped',
      dataIndex: 'shipped_quantity',
      key: 'shipped_quantity',
      width: 100,
      render: (qty) => <Tag color="green">{qty}</Tag>
    },
    {
      title: 'Returned',
      dataIndex: 'returned_quantity',
      key: 'returned_quantity',
      width: 100,
      render: (qty) => <Tag color="red">{qty}</Tag>
    },
    {
      title: 'Net Shipped',
      key: 'net_shipped',
      width: 100,
      render: (_, record) => (
        <Tag color="purple">
          {record.net_shipped}
        </Tag>
      )
    },
    {
      title: 'Remaining',
      dataIndex: 'remaining_quantity',
      key: 'remaining_quantity',
      width: 100,
      render: (qty) => (
        <Tag color={qty > 0 ? 'orange' : 'success'}>
          {qty}
        </Tag>
      )
    },
    {
      title: 'Progress',
      key: 'progress',
      width: 150,
      render: (_, record) => (
        <Progress
          percent={getProgressPercent(record)}
          size="small"
          status={record.remaining_quantity === 0 ? 'success' : 'active'}
        />
      )
    }
  ];

  const itemColumns = [
    {
      title: 'Product',
      dataIndex: 'product_name',
      key: 'product_name',
    },
    {
      title: 'Available',
      key: 'available',
      render: (_, record, index) => {
        const status = form.getFieldValue('status');
        let available = 0;

        if (status === 'picked') {
          available = record.total_quantity - record.picked_quantity;
        } else if (status === 'shipped' || status === 'in_transit') {
          available = record.picked_quantity - record.shipped_quantity;
        } else if (status === 'returned') {
          available = record.shipped_quantity - record.returned_quantity;
        } else {
          available = record.remaining_quantity;
        }

        return <Tag color="blue">{available}</Tag>;
      }
    },
    {
      title: 'Quantity',
      key: 'quantity',
      render: (_, record, index) => {
        const status = form.getFieldValue('status');
        let maxQty = 0;

        if (status === 'picked') {
          maxQty = record.total_quantity - record.picked_quantity;
        } else if (status === 'shipped' || status === 'in_transit') {
          maxQty = record.picked_quantity - record.shipped_quantity;
        } else if (status === 'returned') {
          maxQty = record.shipped_quantity - record.returned_quantity;
        } else {
          maxQty = record.remaining_quantity;
        }

        return (
          <InputNumber
            min={0}
            max={maxQty}
            value={selectedItems[index]?.quantity || 0}
            onChange={(value) => handleQuantityChange(index, value)}
            style={{ width: '100%' }}
          />
        );
      }
    }
  ];

  if (!trackingData) {
    return <Card loading={loading}>Loading tracking data...</Card>;
  }

  return (
    <div style={{ padding: '24px' }}>
      <Card
        title={
          <Space>
            <TruckOutlined style={{ fontSize: '24px', color: '#1890ff' }} />
            <span>Order Tracking - {trackingData.sale.invoice_number ?? trackingData.sale.receipt_number}</span>
          </Space>
        }
        extra={
          <Space>
            <Button onClick={() => setHistoryVisible(true)}>View History</Button>
            <Button type="primary" disabled={trackingData.sale.allocation_status == 'closed'} onClick={() => setModalVisible(true)}>
              Update Status
            </Button>
          </Space>
        }
      >
        <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
          <Col xs={24} md={12} lg={8}>
            <Card>
              <Statistic
                title="Total Items"
                value={trackingData.tracking_summary.reduce((sum, item) => sum + parseFloat(item.total_quantity), 0)}
                prefix={<ShopOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} md={12} lg={8}>
            <Card>
              <Statistic
                title="Shipped Items"
                value={trackingData.tracking_summary.reduce((sum, item) => sum + parseFloat(item.shipped_quantity), 0)}
                valueStyle={{ color: '#3f8600' }}
                prefix={<TruckOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} md={12} lg={8}>
            <Card>
              <Statistic
                title="Remaining Items"
                value={trackingData.tracking_summary.reduce((sum, item) => sum + parseFloat(item.remaining_quantity), 0)}
                valueStyle={{ color: '#cf1322' }}
                prefix={<ClockCircleOutlined />}
              />
            </Card>
          </Col>
        </Row>

        <Descriptions bordered column={2} style={{ marginBottom: '24px' }}>
          <Descriptions.Item label="Invoice Number">
            {trackingData.sale.invoice_number}
          </Descriptions.Item>
          <Descriptions.Item label="Customer">
            {trackingData.sale.customer_name || 'N/A'}
          </Descriptions.Item>
          <Descriptions.Item label="Total Amount">
            {trackingData.sale.total}
          </Descriptions.Item>
          <Descriptions.Item label="Status">
            <Tag color={getStatusColor(trackingData.sale.status)}>
              {trackingData.sale.status.toUpperCase()}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Allocation Status">
            <Tag
              icon={getStatusIcon(trackingData.allocation_status)}
              color={getStatusColor(trackingData.allocation_status)}
            >
              {trackingData.allocation_status.toUpperCase()}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Created" span={2}>
            {new Date(trackingData.sale.created_at).toLocaleString()}
          </Descriptions.Item>
        </Descriptions>

        <Table
          columns={columns}
          dataSource={trackingData.tracking_summary}
          rowKey="item_id"
          pagination={false}
          loading={loading}
          scroll={{ x: 1000 }}
        />
      </Card>

      <Modal
        title="Update Order Status"
        visible={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        footer={null}
        width={900}
      >
        <Paragraph style={{ textAlign: 'center', marginBottom: 20 }}>
          <strong>Order Flow:</strong><br />
          <span style={{ background: '#e6f7ff', padding: '5px 10px', borderRadius: '12px', color: '#1890ff', fontWeight: '500' }}>Picked</span>
          <span style={{ margin: '0 10px', color: '#1890ff', fontWeight: 'bold' }}>→</span>
          <span style={{ background: '#e6f7ff', padding: '5px 10px', borderRadius: '12px', color: '#1890ff', fontWeight: '500' }}>Shipped</span>
          <span style={{ margin: '0 10px', color: '#1890ff', fontWeight: 'bold' }}>→</span>
          <span style={{ background: '#e6f7ff', padding: '5px 10px', borderRadius: '12px', color: '#1890ff', fontWeight: '500' }}>In Transit</span>
          <span style={{ margin: '0 10px', color: '#1890ff', fontWeight: 'bold' }}>→</span>
          <span style={{ background: '#f6ffed', padding: '5px 10px', borderRadius: '12px', color: '#52c41a', fontWeight: '500' }}>Closed</span>
        </Paragraph>


        <Form form={form} layout="vertical" onFinish={handleStatusUpdate}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Status"
                name="status"
                rules={[{ required: true, message: 'Please select status' }]}
              >
                <Select placeholder="Select status">
                  <Option value="released">Released</Option>
                  <Option value="picked">Picked</Option>
                  <Option value="shipped">Shipped</Option>
                  <Option value="in_transit">In Transit</Option>
                  <Option value="returned">Returned</Option>
                  <Option disabled={trackingData.sale.allocation_status != 'shipped' || trackingData.sale.allocation_status != 'in_transit'} value="closed">Closed</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Table
            columns={itemColumns}
            dataSource={trackingData.tracking_summary}
            rowKey="item_id"
            pagination={false}
            size="small"
            style={{ marginBottom: '16px' }}
          />

          <Form.Item label="Notes" name="notes">
            <TextArea rows={4} placeholder="Add any notes about this status update" />
          </Form.Item>

          <Form.Item>
            <Space style={{ float: 'right' }}>
              <Button onClick={() => setModalVisible(false)}>Cancel</Button>
              <Button type="primary" htmlType="submit" loading={loading}>
                Update Status
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Tracking History"
        visible={historyVisible}
        onCancel={() => setHistoryVisible(false)}
        footer={null}
        width={800}
      >
        <Timeline mode="left">
          {trackingData.tracking_history.map((item, index) => (
            <Timeline.Item
              key={index}
              color={getStatusColor(item.status)}
              dot={getStatusIcon(item.status)}
            >
              <Card size="small">
                <Space direction="vertical" style={{ width: '100%' }}>
                  <div>
                    <strong>Status:</strong>{' '}
                    <Tag color={getStatusColor(item.status)}>
                      {item.status.toUpperCase()}
                    </Tag>
                  </div>
                  {item.retailer && (
                    <div>
                      <strong>Retailer:</strong> {item.retailer.name}
                    </div>
                  )}
                  {item.notes && (
                    <div>
                      <strong>Notes:</strong> {item.notes}
                    </div>
                  )}
                  <div>
                    <strong>Updated By:</strong> {item.updated_by?.name || 'System'}
                  </div>
                  <div>
                    <strong>Date:</strong>{' '}
                    {new Date(item.status_date).toLocaleString()}
                  </div>
                </Space>
              </Card>
            </Timeline.Item>
          ))}
        </Timeline>
      </Modal>
    </div>
  );
};

export default OrderTracking;